import { useMemo } from 'react'
import {
  flowArrows,
  lineNumberLabels,
  textLinks,
  LINK_RULE_COLORS,
  type EditorLayers,
  type LayerEdge,
  type LayerNode,
  type LayerRect,
} from './editorLayers'

// VISN-360: the editor layers, drawn inside the renderer's canvas group (image pixels shifted by half the
// image, like every other shape). Read-only: nothing here is clickable or saved.

const FLOW_COLOR = '#2563eb'
const LINE_NUMBER_COLOR = '#0e7490'

interface EditorLayersOverlayProps {
  layers: EditorLayers
  rectangles: LayerRect[]
  edges: LayerEdge[]
  nodes: Map<string, LayerNode>
  imageSize: { width: number; height: number }
}

export default function EditorLayersOverlay({ layers, rectangles, edges, nodes, imageSize }: EditorLayersOverlayProps) {
  const arrows = useMemo(() => (layers.flow ? flowArrows(rectangles, edges, nodes) : []), [layers.flow, rectangles, edges, nodes])
  const links = useMemo(() => (layers.textLinks ? textLinks(rectangles, edges, nodes) : []), [layers.textLinks, rectangles, edges, nodes])
  const labels = useMemo(() => (layers.lineNumbers ? lineNumberLabels(rectangles, nodes) : []), [layers.lineNumbers, rectangles, nodes])
  const ox = imageSize.width / 2
  const oy = imageSize.height / 2
  // Marks scale with the drawing (a ~10,000 px sheet gets ~1:1 pixel sizes).
  const u = Math.max(imageSize.width, imageSize.height) / 10000
  const a = 26 * u // arrowhead half-length
  return (
    <g className="editor-layers" style={{ pointerEvents: 'none' }}>
      {links.map((l) => (
        <line
          key={l.key}
          data-testid="text-link"
          data-rule={l.rule}
          x1={l.x1 - ox}
          y1={l.y1 - oy}
          x2={l.x2 - ox}
          y2={l.y2 - oy}
          stroke={LINK_RULE_COLORS[l.rule]}
          strokeWidth={5 * u}
          strokeLinecap="round"
        />
      ))}
      {arrows.map((ar) => (
        <path
          key={ar.key}
          data-testid="flow-arrow"
          d={`M ${a} 0 L ${-a * 0.7} ${a * 0.7} L ${-a * 0.7} ${-a * 0.7} Z`}
          transform={`translate(${ar.x - ox} ${ar.y - oy}) rotate(${ar.angle})`}
          fill={FLOW_COLOR}
          stroke="#ffffff"
          strokeWidth={3 * u}
        />
      ))}
      {labels.map((lb) => (
        <text
          key={lb.key}
          data-testid="line-number-label"
          transform={`translate(${lb.x - ox} ${lb.y - oy}) rotate(${lb.angle})`}
          y={-14 * u}
          textAnchor="middle"
          fontSize={30 * u}
          fontFamily="monospace"
          fill={LINE_NUMBER_COLOR}
          stroke="#ffffff"
          strokeWidth={6 * u}
          paintOrder="stroke"
        >
          {lb.text}
        </text>
      ))}
    </g>
  )
}
